'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { LocaleLink } from '@/core/i18n';
import { ArrowRight, ChevronDown, CheckCircle2, Quote } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import type { LucideIcon } from 'lucide-react';
import { usePageContent } from '@/core/i18n/content';
import { TEMPLATE_CHROME } from '@/core/i18n/content/template-chrome.content';
import type { TemplateChromeContent } from '@/core/i18n/content/template-chrome.types';
import { SEO_PRODUCT_TRANSLATIONS, type SEOProductText } from '@/core/i18n/content/seo-product.content';
import { useLocaleContext } from '@/core/i18n/locale.context';
import { useLocaleStore } from '@/core/i18n/locale.store';
import { SEO_LANDING_CONFIGS } from './seo-landing.data';

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

export interface SEOFeature {
  icon: LucideIcon;
  title: string;
  description: string;
}

export interface SEOBenefit {
  stat: string;
  label: string;
  description: string;
}

export interface SEOTestimonial {
  quote: string;
  name: string;
  role: string;
  hotel: string;
  initials: string;
}

export interface SEORelatedLink {
  href: string;
  label: string;
  description: string;
}

export interface SEOProductLandingConfig {
  slug: string;
  heroTitle: string;
  heroSubtitle: string;
  heroCtaText: string;
  problemTitle: string;
  problemPoints: string[];
  features: SEOFeature[];
  benefitsTitle: string;
  benefits: SEOBenefit[];
  testimonial: SEOTestimonial;
  faqItems: { question: string; answer: string }[];
  ctaTitle: string;
  ctaSubtitle: string;
  relatedPages: SEORelatedLink[];
}

/* -------------------------------------------------------------------------- */
/*  Section — Hero                                                            */
/* -------------------------------------------------------------------------- */

function SEOHero({ config, t }: { config: SEOProductLandingConfig; t: TemplateChromeContent }) {
  return (
    <section id={`seo-${config.slug}-hero`} className="relative w-full overflow-hidden bg-hms-bg">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(200,145,58,0.08) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-4xl px-4 pb-16 pt-24 text-center sm:px-6 sm:pt-32 lg:px-8 lg:pt-40">
        <h1
          id={`seo-${config.slug}-title`}
          className={cn(
            'font-poppins text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {config.heroTitle}
        </h1>
        <p id={`seo-${config.slug}-subtitle`} className="mx-auto mt-6 max-w-2xl text-base text-hms-textSub sm:text-lg">
          {config.heroSubtitle}
        </p>
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink
            id={`seo-${config.slug}-cta-primary`}
            href="/register-hotel"
            className={cn(
              'inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-bg shadow-lg shadow-hms-gold/20',
              'transition-all duration-200 hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
              'w-full sm:w-auto justify-center',
            )}
          >
            {t.cta.trialBtn}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
          <LocaleLink
            id={`seo-${config.slug}-cta-demo`}
            href="/contact"
            className={cn(
              'inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-text',
              'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold',
              'w-full sm:w-auto',
            )}
          >
            {t.cta.bookDemo}
          </LocaleLink>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — Problem Statement                                               */
/* -------------------------------------------------------------------------- */

function SEOProblem({ config }: { config: SEOProductLandingConfig }) {
  return (
    <section id={`seo-${config.slug}-problem`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`seo-${config.slug}-problem-title`}
          className="text-center text-2xl font-bold tracking-tight text-hms-text sm:text-3xl"
        >
          {config.problemTitle}
        </h2>
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {config.problemPoints.map((point, idx) => (
            <div
              key={idx}
              id={`seo-${config.slug}-problem-${idx}`}
              className="flex items-start gap-3 rounded-xl border border-hms-border bg-hms-card p-4"
            >
              <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-hms-red/10">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" className="h-3 w-3 text-hms-red">
                  <path d="M8 4v4m0 3h.01" strokeLinecap="round" />
                </svg>
              </div>
              <p className="text-sm leading-relaxed text-hms-textSub">{point}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — Features                                                        */
/* -------------------------------------------------------------------------- */

function SEOFeatures({ config, t }: { config: SEOProductLandingConfig; t: TemplateChromeContent }) {
  return (
    <section id={`seo-${config.slug}-features`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(200,145,58,0.04) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 id={`seo-${config.slug}-features-title`} className={cn(
          'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
          'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
        )}>
          {t.common.keyFeatures}
        </h2>
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {config.features.map((feat, idx) => {
            const Icon = feat.icon;
            return (
              <div
                key={feat.title}
                id={`seo-${config.slug}-feature-${idx}`}
                className={cn(
                  'group rounded-xl border border-hms-border bg-hms-card p-6',
                  'transition-all duration-300 hover:border-hms-gold/20 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/20',
                )}
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-hms-gold/10 group-hover:bg-hms-gold/15 transition-colors">
                  <Icon className="h-5 w-5 text-hms-gold" />
                </div>
                <h3 className="mb-2 text-base font-bold text-hms-text tracking-tight">{feat.title}</h3>
                <p className="text-sm leading-relaxed text-hms-textSub">{feat.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — Benefits                                                        */
/* -------------------------------------------------------------------------- */

function SEOBenefits({ config }: { config: SEOProductLandingConfig }) {
  return (
    <section id={`seo-${config.slug}-benefits`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 id={`seo-${config.slug}-benefits-title`} className={cn(
          'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
          'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
        )}>
          {config.benefitsTitle}
        </h2>
        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {config.benefits.map((b, idx) => (
            <div key={idx} id={`seo-${config.slug}-benefit-${idx}`} className="rounded-xl border border-hms-border bg-hms-card p-6 text-center">
              <p className={cn(
                'text-3xl font-extrabold tracking-tight',
                'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
              )}>
                {b.stat}
              </p>
              <p className="mt-2 text-sm font-semibold text-hms-text">{b.label}</p>
              <p className="mt-1 text-xs leading-relaxed text-hms-textDim">{b.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — Testimonial                                                     */
/* -------------------------------------------------------------------------- */

function SEOTestimonialSection({ config }: { config: SEOProductLandingConfig }) {
  const t = config.testimonial;
  return (
    <section id={`seo-${config.slug}-testimonial`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-xl border border-hms-border bg-hms-card p-8 text-center">
          <Quote className="mx-auto mb-4 h-8 w-8 text-hms-gold/20" />
          <p className="text-base italic leading-relaxed text-hms-textSub sm:text-lg">&ldquo;{t.quote}&rdquo;</p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-hms-gold/20">
              <span className="text-xs font-bold text-hms-gold">{t.initials}</span>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-hms-text">{t.name}</p>
              <p className="text-xs text-hms-textDim">{t.role}, {t.hotel}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — FAQ                                                             */
/* -------------------------------------------------------------------------- */

function SEOFAQ({ config, t }: { config: SEOProductLandingConfig; t: TemplateChromeContent }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const toggle = useCallback((i: number) => setOpenIndex((p) => (p === i ? null : i)), []);

  return (
    <section id={`seo-${config.slug}-faq`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <h2 id={`seo-${config.slug}-faq-title`} className={cn(
          'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
          'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
        )}>
          {t.common.faq}
        </h2>
        <div className="mt-12 space-y-3">
          {config.faqItems.map((item, idx) => (
            <div
              key={idx}
              id={`seo-${config.slug}-faq-item-${idx}`}
              className={cn(
                'rounded-xl border transition-colors',
                openIndex === idx ? 'border-hms-gold/30 bg-hms-card' : 'border-hms-border bg-hms-card/50 hover:border-hms-gold/15',
              )}
            >
              <button
                id={`seo-${config.slug}-faq-toggle-${idx}`}
                type="button"
                onClick={() => toggle(idx)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                aria-expanded={openIndex === idx}
              >
                <span className={cn('text-sm font-semibold leading-snug', openIndex === idx ? 'text-hms-gold' : 'text-hms-text')}>
                  {item.question}
                </span>
                <ChevronDown className={cn('h-4 w-4 shrink-0 transition-transform duration-200', openIndex === idx ? 'rotate-180 text-hms-gold' : 'text-hms-textDim')} />
              </button>
              {openIndex === idx && (
                <div className="px-5 pb-5">
                  <p className="text-sm leading-relaxed text-hms-textSub">{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — Related Pages                                                   */
/* -------------------------------------------------------------------------- */

function SEORelated({ config, t }: { config: SEOProductLandingConfig; t: TemplateChromeContent }) {
  if (config.relatedPages.length === 0) return null;
  return (
    <section id={`seo-${config.slug}-related`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 className="text-center text-2xl font-bold tracking-tight text-hms-text sm:text-3xl">{t.common.exploreMore}</h2>
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {config.relatedPages.map((page, idx) => (
            <LocaleLink
              key={page.href}
              id={`seo-${config.slug}-related-${idx}`}
              href={page.href}
              className={cn(
                'group flex items-start gap-3 rounded-xl border border-hms-border bg-hms-card p-5',
                'transition-all duration-200 hover:border-hms-gold/20 hover:-translate-y-0.5',
              )}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-hms-text group-hover:text-hms-gold transition-colors">{page.label}</p>
                <p className="mt-1 text-xs leading-relaxed text-hms-textDim">{page.description}</p>
              </div>
              <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-hms-textDim group-hover:text-hms-gold transition-colors" />
            </LocaleLink>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — CTA                                                             */
/* -------------------------------------------------------------------------- */

function SEOCTA({ config, t }: { config: SEOProductLandingConfig; t: TemplateChromeContent }) {
  return (
    <section id={`seo-${config.slug}-cta`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(200,145,58,0.06) 0%, transparent 70%)' }} />
      <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <h2 id={`seo-${config.slug}-cta-title`} className={cn(
          'text-3xl font-extrabold tracking-tight sm:text-4xl',
          'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
        )}>
          {config.ctaTitle}
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base text-hms-textSub sm:text-lg">{config.ctaSubtitle}</p>
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink
            id={`seo-${config.slug}-cta-trial`}
            href="/register-hotel"
            className={cn(
              'inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-bg shadow-lg shadow-hms-gold/20',
              'transition-all duration-200 hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
              'w-full sm:w-auto justify-center',
            )}
          >
            {t.cta.trialBtn}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
          <LocaleLink
            id={`seo-${config.slug}-cta-contact`}
            href="/contact"
            className={cn(
              'inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-text',
              'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold',
              'w-full sm:w-auto',
            )}
          >
            {t.cta.contactBtn}
          </LocaleLink>
        </div>
        <p className="mt-5 text-xs text-hms-textDim">{t.common.noCreditCard}</p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Locale merge helper                                                       */
/* -------------------------------------------------------------------------- */

function mergeSEOConfig(
  cfg: SEOProductLandingConfig,
  txt: SEOProductText | undefined,
): SEOProductLandingConfig {
  if (!txt) return cfg;
  return {
    ...cfg,
    heroTitle: txt.heroTitle,
    heroSubtitle: txt.heroSubtitle,
    heroCtaText: txt.heroCtaText,
    problemTitle: txt.problemTitle,
    problemPoints: txt.problemPoints,
    features: cfg.features.map((f, i) => ({
      ...f, // keeps icon
      title: txt.features[i]?.title ?? f.title,
      description: txt.features[i]?.description ?? f.description,
    })),
    benefitsTitle: txt.benefitsTitle,
    benefits: cfg.benefits.map((b, i) => ({
      ...b, // keeps stat
      label: txt.benefits[i]?.label ?? b.label,
      description: txt.benefits[i]?.description ?? b.description,
    })),
    testimonial: {
      ...cfg.testimonial, // keeps name, initials
      quote: txt.testimonial.quote,
      role: txt.testimonial.role,
      hotel: txt.testimonial.hotel,
    },
    faqItems: txt.faqItems,
    ctaTitle: txt.ctaTitle,
    ctaSubtitle: txt.ctaSubtitle,
    relatedPages: cfg.relatedPages.map((r, i) => ({
      ...r, // keeps href
      label: txt.relatedPages[i]?.label ?? r.label,
      description: txt.relatedPages[i]?.description ?? r.description,
    })),
  };
}

/* -------------------------------------------------------------------------- */
/*  Main Component                                                            */
/* -------------------------------------------------------------------------- */

export function SEOProductLanding({ slug }: { slug: string }) {
  const baseConfig = SEO_LANDING_CONFIGS[slug];
  const t = usePageContent(TEMPLATE_CHROME);
  const ctxLocale = useLocaleContext();
  const storeLocale = useLocaleStore((s) => s.locale);
  const locale = ctxLocale ?? storeLocale;
  const localeTexts = locale !== 'en' ? SEO_PRODUCT_TRANSLATIONS[locale] : undefined;
  const translated = localeTexts?.[slug];
  const config = mergeSEOConfig(baseConfig, translated);
  return (
    <main id={`seo-${config.slug}-page`}>
      <SEOHero config={config} t={t} />
      <SEOProblem config={config} />
      <SEOFeatures config={config} t={t} />
      <SEOBenefits config={config} />
      <SEOTestimonialSection config={config} />
      <SEOFAQ config={config} t={t} />
      <SEORelated config={config} t={t} />
      <SEOCTA config={config} t={t} />
    </main>
  );
}
